/**
 * Shared activity-feed filter model (ENG-2357). Used by order, invoice, and
 * inventory activity drawers — org-wide and per-entity.
 */

export type ActivityDateFilter = 'all' | 'today' | 'last24h' | 'last48h' | 'thisWeek';

export const ACTIVITY_DATE_FILTER_LABELS: Record<ActivityDateFilter, string> = {
    all: 'All Time',
    today: 'Today',
    last24h: 'Last 24 Hours',
    last48h: 'Last 48 Hours',
    thisWeek: 'This Week',
};

/** Inclusive lower bound for a date filter, or null for "All time". */
export function activityDateFilterSince(filter: ActivityDateFilter): Date | null {
    const now = new Date();
    switch (filter) {
        case 'today':
            return new Date(now.getFullYear(), now.getMonth(), now.getDate());
        case 'last24h':
            return new Date(now.getTime() - 24 * 60 * 60 * 1000);
        case 'last48h':
            return new Date(now.getTime() - 48 * 60 * 60 * 1000);
        case 'thisWeek': {
            const day = now.getDay();
            const diff = day === 0 ? 6 : day - 1; // Monday as start of week
            return new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
        }
        case 'all':
        default:
            return null;
    }
}

export interface ActivityFeedUserOption {
    id: string;
    label: string;
}

export interface ActivityFeedEventTypeOption {
    value: string;
    label: string;
}

export interface ActivityFeedEventTypeGroup {
    title: string;
    items: ActivityFeedEventTypeOption[];
}

export interface ActivityFeedFiltersValue {
    /** Empty = all types. Values are API eventType strings (or synthetic keys expanded via mapEventTypesForApi). */
    eventTypes: string[];
    dateFilter: ActivityDateFilter;
    userIds: string[];
    /**
     * Server: omit internal/staff actors. Also omits Carl-sourced rows
     * (`source === 'carl-agent'`); null `userId` rows stay unless excludeGenerated.
     */
    excludeInternalActors: boolean;
    /**
     * Server (all feeds): omit `source === 'carl-agent'` and `userId == null`.
     */
    excludeGenerated: boolean;
}

export const EMPTY_ACTIVITY_FEED_FILTERS: ActivityFeedFiltersValue = {
    eventTypes: [],
    dateFilter: 'all',
    userIds: [],
    excludeInternalActors: false,
    excludeGenerated: false,
};

export function hasActiveActivityFeedFilters(filters: ActivityFeedFiltersValue): boolean {
    return (
        filters.eventTypes.length > 0
        || filters.dateFilter !== 'all'
        || filters.userIds.length > 0
        || filters.excludeInternalActors
        || filters.excludeGenerated
    );
}

/**
 * Query params shared by order / invoice / inventory activity-log POSTs.
 * Exclude flags are applied server-side (see field docs on ActivityFeedFiltersValue).
 */
export interface ActivityFeedApiFilterParams {
    eventTypes?: string[];
    userIds?: string[];
    since?: string;
    /** Omit staff actors and Carl-sourced rows; keep null userId unless excludeGenerated. */
    excludeInternalActors?: boolean;
    /** Omit Carl-sourced rows and null userId (all feeds). */
    excludeGenerated?: boolean;
}

/**
 * Maps UI filter state to API body fields (omit empty / default).
 * Pass `mapEventTypes` when the UI uses synthetic values (e.g. order "Order Placed").
 */
export function activityFeedFiltersToApiParams(
    filters: ActivityFeedFiltersValue,
    mapEventTypes?: (selected: string[]) => string[],
): ActivityFeedApiFilterParams {
    const eventTypes = filters.eventTypes.length > 0
        ? (mapEventTypes ? mapEventTypes(filters.eventTypes) : filters.eventTypes)
        : undefined;
    const sinceDate = activityDateFilterSince(filters.dateFilter);
    return {
        ...(eventTypes && eventTypes.length > 0 ? { eventTypes } : {}),
        ...(filters.userIds.length > 0 ? { userIds: filters.userIds } : {}),
        ...(sinceDate ? { since: sinceDate.toISOString() } : {}),
        ...(filters.excludeInternalActors ? { excludeInternalActors: true } : {}),
        ...(filters.excludeGenerated ? { excludeGenerated: true } : {}),
    };
}
